<template>
  <div ref="mapRoot" class="us-map">
    <svg viewBox="0 0 960 600" role="img" aria-label="United States map">
      <g v-for="state in projectedStates" :key="state.name">
        <path
          :d="state.path"
          :fill="state.isContactState ? '#2274C6' : '#ffffff'"
          stroke="#000000"
          stroke-width="1"
          :class="{ clickable: state.isContactState }"
          @mouseenter="showTooltip($event, state)"
          @mousemove="moveTooltip($event, state)"
          @mouseleave="hideTooltip"
          @click="handleStateClick(state)"
        />
        <text
          v-if="state.isContactState && state.abbr"
          :x="state.labelX"
          :y="state.labelY"
          :style="{ fontSize: `${labelFontSize}px` }"
          text-anchor="middle"
          dominant-baseline="middle"
          class="state-label"
        >
          {{ state.abbr }}
        </text>
      </g>
    </svg>
    <div
      v-if="tooltip.visible"
      class="tooltip bs-tooltip-top show map-tooltip"
      :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }"
      role="tooltip"
    >
      <div class="tooltip-arrow"></div>
      <div class="tooltip-inner">{{ tooltip.text }}</div>
    </div>
  </div>
</template>

<style>
.us-map {
  margin: 0 auto;
  max-width: 860px;
  position: relative;
  width: 100%;
}

.us-map svg {
  display: block;
  height: auto;
  width: 100%;
}

.clickable {
  cursor: pointer;
}

.state-label {
  fill: #ffffff;
  font-family: Source Sans Pro, Arial, Helvetica, sans-serif;
  font-weight: 700;
  paint-order: stroke fill;
  pointer-events: none;
  stroke: #000000;
  stroke-width: 1px;
  stroke-linejoin: round;
}

.map-tooltip {
  position: fixed;
  transform: translate(-50%, calc(-100% - 10px));
  pointer-events: none;
  z-index: 5000;
}
.map-tooltip .tooltip-arrow {
  position: absolute;
  left: 50% !important;
  bottom: -5px;
  width: 12px;
  height: 6px;
  transform: translateX(-50%) !important;
  border: 0 !important;
}
.map-tooltip .tooltip-arrow::before {
  content: "" !important;
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  border-left: 6px solid transparent !important;
  border-right: 6px solid transparent !important;
  border-top: 6px solid var(--bs-tooltip-bg, #000) !important;
  border-bottom: 0 !important;
}

</style>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import usa from "../data/usa.json";
import contacts from "../data/contacts.json";

const emit = defineEmits(["stateSelected"]);
const VIEWBOX_WIDTH = 960;
const VIEWBOX_HEIGHT = 600;
const MAP_PADDING = 20;
const REFERENCE_LATITUDE = 37.8;
const REFERENCE_LATITUDE_RAD = (REFERENCE_LATITUDE * Math.PI) / 180;

const excludedStates = new Set(["Alaska", "Hawaii"]);

const normalizeStateName = (value) => {
  return value.toLowerCase().replace(/[^a-z]/g, "");
};

const projectLongitude = (lon) => lon * Math.cos(REFERENCE_LATITUDE_RAD);
const baseLabelFontSize = Number(usa?.labelSettings?.fontSize) || 12;
const minLabelFontSize = Number(usa?.labelSettings?.minFontSize) || 12;
const maxLabelFontSize = Number(usa?.labelSettings?.maxFontSize) || 48;
const labelScaleExponent = Number(usa?.labelSettings?.scaleExponent) || 0.6;
const mapRoot = ref(null);
const mapWidth = ref(VIEWBOX_WIDTH);
let resizeObserver = null;

const labelFontSize = computed(() => {
  // Keep label text legible as map shrinks by inversely scaling
  // text units against current rendered map width.
  const renderScale = mapWidth.value / VIEWBOX_WIDTH || 1;
  const scaledFontSize = baseLabelFontSize / Math.pow(renderScale, labelScaleExponent);
  return Math.max(minLabelFontSize, Math.min(maxLabelFontSize, scaledFontSize));
});

const availableStateNames = computed(() => {
  const names = new Set();
  contacts.forEach((entry) => {
    entry.state
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((name) => names.add(normalizeStateName(name)));
  });
  return names;
});

const contiguousStates = computed(() => {
  return usa.features.filter((feature) => {
    const stateName = feature.properties?.name || "";
    return !excludedStates.has(stateName);
  });
});

const bounds = computed(() => {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  const recordPoint = (coordinate) => {
    const [lon, lat] = coordinate;
    const projectedLon = projectLongitude(lon);
    if (projectedLon < minLon) minLon = projectedLon;
    if (projectedLon > maxLon) maxLon = projectedLon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  };

  contiguousStates.value.forEach((feature) => {
    const polygons =
      feature.geometry.type === "Polygon"
        ? [feature.geometry.coordinates]
        : feature.geometry.coordinates;

    polygons.forEach((polygon) => {
      polygon.forEach((ring) => {
        ring.forEach(recordPoint);
      });
    });
  });

  return { minLon, maxLon, minLat, maxLat };
});

const projectionMeta = computed(() => {
  const { minLon, maxLon, minLat, maxLat } = bounds.value;
  const lonRange = maxLon - minLon || 1;
  const latRange = maxLat - minLat || 1;

  const drawableWidth = VIEWBOX_WIDTH - MAP_PADDING * 2;
  const drawableHeight = VIEWBOX_HEIGHT - MAP_PADDING * 2;
  const scale = Math.min(drawableWidth / lonRange, drawableHeight / latRange);

  const projectedWidth = lonRange * scale;
  const projectedHeight = latRange * scale;
  const offsetX = (VIEWBOX_WIDTH - projectedWidth) / 2;
  const offsetY = (VIEWBOX_HEIGHT - projectedHeight) / 2;

  return {
    minLon,
    maxLat,
    scale,
    offsetX,
    offsetY,
  };
});

const project = (lon, lat) => {
  const { minLon, maxLat, scale, offsetX, offsetY } = projectionMeta.value;
  const projectedLon = projectLongitude(lon);
  const x = offsetX + (projectedLon - minLon) * scale;
  const y = offsetY + (maxLat - lat) * scale;
  return [x, y];
};

const ringToPath = (ring) => {
  if (!ring.length) return "";
  const [startX, startY] = project(ring[0][0], ring[0][1]);
  let path = `M ${startX.toFixed(2)} ${startY.toFixed(2)}`;
  for (let i = 1; i < ring.length; i += 1) {
    const [x, y] = project(ring[i][0], ring[i][1]);
    path += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  return `${path} Z`;
};

const featureToPath = (feature) => {
  const polygons =
    feature.geometry.type === "Polygon"
      ? [feature.geometry.coordinates]
      : feature.geometry.coordinates;

  const parts = [];
  polygons.forEach((polygon) => {
    polygon.forEach((ring) => {
      const part = ringToPath(ring);
      if (part) parts.push(part);
    });
  });
  return parts.join(" ");
};

const getLabelCenter = (feature) => {
  const properties = feature.properties || {};
  const hasCustomLonLat =
    typeof properties.labelLon === "number" &&
    typeof properties.labelLat === "number";
  const labelOffsetX =
    typeof properties.labelOffsetX === "number" ? properties.labelOffsetX : 0;
  const labelOffsetY =
    typeof properties.labelOffsetY === "number" ? properties.labelOffsetY : 0;

  if (hasCustomLonLat) {
    const [customX, customY] = project(properties.labelLon, properties.labelLat);
    return { x: customX + labelOffsetX, y: customY + labelOffsetY };
  }

  const polygons =
    feature.geometry.type === "Polygon"
      ? [feature.geometry.coordinates]
      : feature.geometry.coordinates;
  let lonSum = 0;
  let latSum = 0;
  let count = 0;
  polygons.forEach((polygon) => {
    polygon.forEach((ring) => {
      ring.forEach(([lon, lat]) => {
        lonSum += lon;
        latSum += lat;
        count += 1;
      });
    });
  });
  const [x, y] = project(lonSum / count, latSum / count);
  return { x: x + labelOffsetX, y: y + labelOffsetY };
};

const projectedStates = computed(() => {
  return contiguousStates.value.map((feature) => {
    const name = feature.properties.name;
    const abbr = (feature.properties.iso_3166_2 || "").toUpperCase();
    const isContactState = availableStateNames.value.has(normalizeStateName(name));
    const center = getLabelCenter(feature);

    return {
      name,
      abbr,
      isContactState,
      path: featureToPath(feature),
      labelX: center.x,
      labelY: center.y,
    };
  });
});

const tooltip = reactive({
  visible: false,
  text: "",
  x: 0,
  y: 0,
});

const showTooltip = (event, state) => {
  if (!state?.isContactState || !state?.abbr) {
    hideTooltip();
    return;
  }
  tooltip.visible = true;
  tooltip.text = state.name;
  tooltip.x = event.clientX;
  tooltip.y = event.clientY;
};

const moveTooltip = (event, state) => {
  if (!state?.isContactState || !state?.abbr) {
    hideTooltip();
    return;
  }
  tooltip.x = event.clientX;
  tooltip.y = event.clientY;
};

const hideTooltip = () => {
  tooltip.visible = false;
};

const handleStateClick = (state) => {
  if (!state.isContactState) return;
  emit("stateSelected", state.name);
};

onMounted(() => {
  if (!mapRoot.value) return;
  mapWidth.value = mapRoot.value.clientWidth || VIEWBOX_WIDTH;
  resizeObserver = new ResizeObserver((entries) => {
    if (!entries.length) return;
    mapWidth.value = entries[0].contentRect.width || VIEWBOX_WIDTH;
  });
  resizeObserver.observe(mapRoot.value);
});

onUnmounted(() => {
  hideTooltip();
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>